import { useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  Field,
  reduxForm,
  formValueSelector,
  SubmissionError,
} from "redux-form";
import { Button, Input } from "expano-components";

import { Main, Dashboard, Select } from "components";

import useGet from "hooks/useGet";
import useCreate from "hooks/useCreate";

import origin_countries_options from "utils/select_options/origin_countries";
import product_kind_options, {
  default_product_kind,
} from "utils/select_options/product_kind_options";

import { ReactComponent as ProductIcon } from "icons/product.svg";

import CnCodeSection from "./components/CnCodeSection";

const form_selector = formValueSelector("create-product-group");

const InputField = (props) => <Input {...props} />;
const SelectField = (props) => <Select {...props} />;

const ProductGroupNew = ({
  handleSubmit,
  change,
  pristine,
  history,
  invalid,
  name,
  root_taxon_id,
  cn_code,
  cn_code_from_ai,
}) => {
  const [producer_id, setProducerId] = useState(null);

  const { data: root_taxons } = useGet({
    query: ["root_taxons"],
    path: "/root_taxons",
    context: "list",
    search: { per_page: 10_000 },
  });

  const { data: producers } = useGet({
    query: ["producers"],
    path: "/producers",
    context: "list",
    search: { per_page: 10_000 },
  });

  const { data: brands } = useGet({
    query: ["brands", producer_id],
    path: "/brands",
    context: "list",
    search: {
      "q[producer_id_eq]": producer_id,
      per_page: 100,
    },
    options: {
      enabled: !!producer_id,
    },
  });

  const { is_creating, create } = useCreate({
    path: "/product_groups",
    onSuccess: ({ id }) => {
      history.push(`/product_groups/${id}`);
    },
    onError: (err) => {
      throw new SubmissionError(
        Object.fromEntries(
          Object.entries(err).map(([key, value]) => {
            if (["producer", "brand", "root_taxon"].includes(key)) {
              return [`${key}_id`, value];
            }
            return [key, value];
          })
        )
      );
    },
  });

  const root_taxon_options = useMemo(() => {
    if (root_taxons?.length > 0) {
      return root_taxons.map(({ id, name, identifier }) => ({
        label: `${name} [${identifier}]`,
        value: id,
      }));
    }
    return [];
  }, [root_taxons]);

  const producer_options = useMemo(() => {
    if (producers?.length > 0) {
      return producers.map(({ id, name }) => ({ label: name, value: id }));
    }
    return [];
  }, [producers]);

  const brand_options = useMemo(() => {
    if (brands?.length > 0) {
      return brands.map(({ id, name }) => ({ label: name, value: id }));
    }
    return [];
  }, [brands]);

  const onSubmit = (values) => create(values);

  return (
    <Main
      page={{
        name: "Dodaj produkt grupowy",
        icon: <ProductIcon />,
        breadcrumbs: [
          {
            name: "Produkty grupowe",
            path: "/product_groups",
          },
          { name: "Dodaj produkt grupowy" },
        ],
      }}
    >
      <div className="dashboard-wrapper-flex">
        <div className="column-half">
          <Dashboard
            header={{ title: "Podstawowe informacje", icon: "info" }}
            footer={
              <>
                <Button
                  type="cancel"
                  onClick={() => history.push("/product_groups")}
                />
                <Button
                  type="save"
                  text={is_creating ? "Proszę czekać..." : "Utwórz"}
                  disabled={pristine || invalid || is_creating}
                  onClick={handleSubmit(onSubmit)}
                />
              </>
            }
          >
            <form
              className="dashboard__form"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              <Field
                name="name"
                label="Nazwa produktu"
                component={InputField}
                type="text"
              />
              <div className="row m-b-10">
                <div className="box-6">
                  <Field
                    label="Rodzaj"
                    name="kind"
                    component={SelectField}
                    options={product_kind_options}
                    defaultValue={product_kind_options.find(
                      ({ value }) => value === default_product_kind
                    )}
                    handleChange={(value) => change("kind", value)}
                  />
                </div>
                <div className="box-6">
                  <p className="additional-info m-t-20">
                    Rodzaju nie można zmienić po utworzeniu. Produkty w grupie
                    dziedziczą rodzaj.
                  </p>
                </div>
              </div>
              <div className="row m-b-10">
                <div className="box-6">
                  <Field
                    label="Kategoria"
                    name="root_taxon_id"
                    component={SelectField}
                    options={root_taxon_options}
                    handleChange={(value) => {
                      change("root_taxon_id", value);
                      // Prefill kodu CN z defaultu kategorii (klasyfikacja AI
                      // per lisc taksonomii) - pracownik potwierdza zapisem.
                      // Aktualizujemy przy kazdej zmianie kategorii, chyba ze
                      // kod zostal wybrany recznie - wtedy nie nadpisujemy.
                      const is_manual = cn_code && !cn_code_from_ai;
                      if (is_manual) return;

                      const taxon = root_taxons?.find(({ id }) => id === value);
                      change("cn_code", taxon?.default_cn_code || null);
                      change("cn_code_from_ai", Boolean(taxon?.default_cn_code));
                    }}
                  />
                </div>
                <div className="box-6">
                  <Field
                    name="sku"
                    label="SKU"
                    component={InputField}
                    type="text"
                  />
                </div>
              </div>
              <div className="row m-b-10">
                <div className="box-6">
                  <Field
                    label="Producent"
                    name="producer_id"
                    component={SelectField}
                    options={producer_options}
                    handleChange={(value) => {
                      change("producer_id", value);
                      change("brand_id", null);
                      setProducerId(value);
                    }}
                  />
                </div>
                <div className="box-6">
                  <Field
                    key={producer_id}
                    label="Marka"
                    name="brand_id"
                    isDisabled={!producer_id}
                    component={SelectField}
                    options={brand_options}
                    handleChange={(value) => {
                      change("brand_id", value);
                    }}
                  />
                </div>
              </div>
              <p className="label m-b-10">
                Dane INTRASTAT (wymagane dla sprzedaży do UE)
              </p>
              <CnCodeSection
                change={change}
                name={name}
                root_taxon_id={root_taxon_id}
              />
              <div className="row m-b-10">
                <div className="box-6">
                  <Field
                    label="Kraj pochodzenia"
                    name="country_of_origin"
                    component={SelectField}
                    options={origin_countries_options}
                    handleChange={(value) => {
                      change("country_of_origin", value);
                    }}
                  />
                </div>
              </div>
            </form>
          </Dashboard>
        </div>
      </div>
    </Main>
  );
};

const validate = (values) => {
  const errors = {};
  if (!values.name) {
    errors.name = "Nazwa produktu jest wymagana!";
  }

  if (!values.sku) {
    errors.sku = "SKU produktu jest wymagane!";
  }

  if (!values.kind) {
    errors.kind = "Rodzaj jest wymagany!";
  }

  if (!values.root_taxon_id) {
    errors.root_taxon_id = "Kategoria jest wymagana!";
  }

  if (!values.producer_id) {
    errors.producer_id = "Producent jest wymagany!";
  }

  if (!values.brand_id) {
    errors.brand_id = "Marka jest wymagana!";
  }

  if (!values.cn_code) {
    errors.cn_code = "Kod CN jest wymagany!";
  } else if (!/^\d{8}$/.test(values.cn_code)) {
    errors.cn_code = "Kod CN musi składać się z 8 cyfr!";
  }

  if (!values.country_of_origin) {
    errors.country_of_origin = "Kraj pochodzenia jest wymagany!";
  }

  return errors;
};

// Stala referencja - enableReinitialize porownuje initialValues przy kazdym
// renderze, nowy obiekt za kazdym razem wyczyscilby wpisane dane.
const initial_values = { kind: default_product_kind };

const mapStateToProps = (state) => ({
  initialValues: initial_values,
  name: form_selector(state, "name"),
  root_taxon_id: form_selector(state, "root_taxon_id"),
  cn_code: form_selector(state, "cn_code"),
  cn_code_from_ai: form_selector(state, "cn_code_from_ai"),
});

export default connect(mapStateToProps)(
  reduxForm({
    form: "create-product-group",
    enableReinitialize: true,
    validate,
  })(ProductGroupNew)
);
